import { Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { EventDelegatorService } from '@services/event-delegator.service';
import { ChartLibraryService } from '@services/chart-library.service';
import type * as leaflet from 'leaflet';
import { escape } from 'lodash';

window.perfCheck = (name, end = false) => {
  if (document.cookie.includes('app_perf')) {
    console[`time${end ? 'End' : ''}`](`perf[${name}]`)
  }
}

 const ICONS = {
  0: './assets/markers/marker-icon-2x-black.png',
  1: './assets/markers/marker-icon-2x-blue.png',
  2: './assets/markers/marker-icon-2x-red.png',
  3: './assets/markers/marker-icon-2x-green.png',
  4: './assets/markers/marker-icon-2x-orange.png',
  5: './assets/markers/marker-icon-2x-yellow.png',
  6: './assets/markers/marker-icon-2x-violet.png',
  7: './assets/markers/marker-icon-2x-pink.png',
  8: './assets/markers/marker-icon-2x-dblue.png',
  9: './assets/markers/marker-icon-2x-darkgreen.png',
  10: './assets/markers/marker-icon-2x-cyan.png',
  11: './assets/markers/marker-icon-2x-brown.png' // default icon
};

 const COLORS = [
  'black',
  'blue',
  'red',
  'green',
  'orange',
  'yellow',
  'violet',
  'pink',
  'lightblue',
  'darkgreen',
  'cyan',
  'brown'
];

declare const L;

@Component({
  selector: 'app-map',
  standalone: true,
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('map', { static: true }) mapElement: ElementRef;
  @Input() data: any;
  private map: leaflet.Map;
  private features: any[] = [];
  private mapControls = false;
  private tier = 0;
  private hideTier: any[] = [];
  private keyboard = true;
  private missingCoords: any;
  private markers: leaflet.LayerGroup;
  private readonly makersLimit = 5000;
  private readonly makersClusterLimit = 50;
  private eventBus: any[] = [];

  constructor(
    private eventDelegatorService: EventDelegatorService,
    private chartLibraryService: ChartLibraryService,
    private zone: NgZone
  ) {}

  get isNewDashboard() {
    return this.data?.isfrom === 'newDashboard';
  }

  get tileSize() {
    return this.isNewDashboard ? 550 : 512;
  }

  get mapZoom() {
    return this.isNewDashboard ? 2 : 3;
  }

  ngOnChanges(): void {
    if (this.data) {
      this.init();
    }
  }

  ngOnInit(): void {
    if (this.data) {
      if (this.isNewDashboard) {
        this.keyboard = false;
      }

      this.init();
    }

    this.eventBus.push(
      this.eventDelegatorService.on('leaflet-export-map', (data) => {
        this.addMarketsGroup(false);
        this.chartLibraryService.exportLeafletImage(this.map, data.filename)
          .then(() => this.addMarketsGroup());
      })
    );

    this.eventBus.push(
      this.eventDelegatorService.on('ngx-event-analyze', (ev) => {
        if (ev.action === 'print-map') {
          const name = `${ev.data.name}_pinmap`;
          this.chartLibraryService.exportLeafletImage(this.map, name);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.map?.remove();
    this.eventBus.forEach((e) => e?.unsubscribe());
  }

  private init() {
    if (this.data.mapData) {
      this.features = this.data.mapData.features || [];
      this.mapControls = this.data.controls;
    } else if (this.data.data) {
      this.features = this.data.data.pinmap.features || [];
      this.mapControls = this.data.data.controls ? this.data.data.controls : false;
    }

    perfCheck('uniqueFeatures');

    this.missingCoords = {};

    this.features = Array.from(this.features
      .reduce<Map<string, any>>((prev, curr) => this.uniqueFeatures(prev, curr), new Map()).values());

    if (Object.keys(this.missingCoords).length > 0) {

      console.warn('missing coordinates', this.missingCoords);
    }

    if (this.features.length > this.makersLimit) {
      this.hideTier = [6, 7, 8, 9, 10];
    }

    perfCheck('uniqueFeatures', true);

    this.zone.runOutsideAngular(() => {
      this.addMap(this.mapElement.nativeElement);
    });
  }

  private uniqueFeatures(prev: Map<string, any>, { id, geometry, properties }) {
    const coords = geometry.coordinates.join('|');

    if (coords === '|') {
      this.missingCoords[properties.name] = { id, geometry, properties };
    }

    if (!prev.has(coords)) {
      prev.set(coords, { id, geometry, properties });
    }

    return prev;
  }

  private addMap(el: HTMLElement) {
    perfCheck('addMap');
    this.map?.remove();

    const mapOptions: leaflet.MapOptions = {
      maxBoundsViscosity: 1.0,
      zoomControl: this.mapControls,
      scrollWheelZoom: this.mapControls,
      touchZoom: this.mapControls,
      preferCanvas: true,
      // doubleClickZoom: this.mapControls,
      boxZoom: this.mapControls,
      attributionControl: false,
      keyboard: this.mapControls
    };
    this.chartLibraryService.addHeadersSupportToLeaflet(L);

    const tile = L.tileLayer(
      this.chartLibraryService.mapURL,
      {
        id: 'mapbox/light-v9',
        tileSize: this.tileSize,
        maxZoom: 18,
        zoomOffset: -1,
        minZoom: 1,
        noWrap: true,
        bounds: [[-90, -180], [90, 180]],
      },
      [
        { header: 'Authorization', value: `Bearer ${localStorage.getItem('auth_token')}` }
      ]
    );

    this.map = L.map(el, mapOptions)
      .setView([15.395158, 19.062826], this.mapZoom)
      .addLayer(tile)
      .whenReady(() => {
        setTimeout(() => {
          this.map.invalidateSize();
          this.loadingFinished();
        }, 100);
      });

    this.addMarketsGroup();
    this.addTierControl();

    perfCheck('addMap', true);
  }

  private addMarketsGroup(clusterEnabled = true) {
    this.markers?.remove();

    this.markers = this.features.length >= this.makersClusterLimit && clusterEnabled
      ? (L as any).markerClusterGroup({ chunkedLoading: false })
      : L.layerGroup([]);

    this.markers.addTo(this.map);
    this.addMarkers();
  }

  private addMarkers() {
    this.markers.clearLayers();

    this.features?.forEach(({ id, geometry, properties }) => {
      this.tier = this.tier <= properties.tier ? properties.tier : this.tier;

      if (!this.hideTier.includes(properties.tier)) {
        const makerOptions: leaflet.MarkerOptions = {
          icon: this.getIcon(properties.tier),
          keyboard: this.keyboard
        };

        const marker = L.marker(geometry.coordinates, makerOptions);
        marker.bindPopup(escape(properties.name), { offset: L.point(0, 14) });
        marker.on('click', () => {
          this.openSidebar({ rid: id, name: properties.name, tier: properties.tier });
        });

        marker.addTo(this.markers);
      }
    });
  }

  private addTierControl() {
    if (!this.mapControls) return;

    const legendTierClick = (ev: MouseEvent) => {
      const target = ev.target as HTMLElement;
      const currentTier = +target.dataset.tier;

      if (isNaN(currentTier)) {
        return;
      }

      if (!this.hideTier.includes(currentTier)) {
        this.hideTier.push(currentTier);
      } else {
        const pos = this.hideTier.indexOf(currentTier);
        this.hideTier.splice(pos, 1);
      }

      target.parentElement.innerHTML = this.getLegendsTemplate();
      this.addMarkers();
    };

    const legend = L.control.attribution({ position: 'bottomleft' });

    legend.onAdd = () => {
      const div = L.DomUtil.create('div', 'infolegend legend');
      div.setAttribute('id', 'legend-wrapper');
      div.innerHTML = this.getLegendsTemplate();
      div.addEventListener('click', legendTierClick);
      return div;
    };

    legend.addTo(this.map);
  }

  private getLegendsTemplate() {
    let elements = '';
    for (let i = 0; i <= this.tier; i++) {
      const addCss = this.hideTier.includes(i) ? 'text-decoration: line-through;' : '';
      elements += `<button aria-label="Tier ${i}" data-tier="${i}"  class="map-legend-label" style="z-index: 1000;${addCss}; background-color:${COLORS[i]}">`;

      elements += `<span style="z-index: -1;"></span> Tier ${i}</button>`;
    }
    return elements;
  }

  private getIcon(tier = 0) {
    const icon = ICONS[tier] || ICONS[11];

    return new L.Icon({
      iconUrl: icon,
      shadowUrl: './assets/markers/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });
  }

  private loadingFinished() {
    this.eventDelegatorService.emitter('custom-ngx-event', {
      type: 'info-card-masonry.reload',
      controller: ''
    });
  }

  private openSidebar(data) {
    this.eventDelegatorService.emitter('custom-ngx-event', {
      type: 'pinmap-events',
      data,
      controller: window.location.pathname
    });
  }
}
